import { useRef, useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { UploadSimpleIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from 'src/context/ToastContext';
import { filesFromDrop } from 'src/lib/file-drop';
import type { UploadFileRejection } from 'src/lib/pricelist-utils';
import { acceptDroppedFiles } from './fileDropzoneAccept';

interface BaseProps {
  /** `accept` attribute for the underlying file input. */
  accept: string;
  /** Instruction line shown inside the dashed area. */
  prompt?: ReactNode;
  buttonLabel?: ReactNode;
  children?: ReactNode;
  /**
   * Validate a candidate file. Return a rejection to surface as a toast and
   * discard the file, or null to accept it. Async so callers can inspect the
   * file's bytes.
   */
  validate: (file: File) => Promise<UploadFileRejection | null>;
  /** Blocks dropping and picking while a file is being processed. */
  disabled?: boolean;
  /** One compact row (icon, prompt, small button) instead of the stacked panel. */
  inline?: boolean;
  className?: string;
}

interface SingleFileProps extends BaseProps {
  multiple?: false;
  /** Called with a file that passed `validate`. */
  onFileAccepted: (file: File) => void;
}

interface MultiFileProps extends BaseProps {
  multiple: true;
  /** Called with every file that passed `validate`. */
  onFilesAccepted: (files: File[]) => void;
}

type Props = SingleFileProps | MultiFileProps;

function DropzonePrompt({
  prompt,
  inline,
}: {
  prompt: ReactNode;
  inline: boolean;
}) {
  if (!prompt) return null;
  return (
    <p
      className={cn(
        'text-muted-foreground',
        inline ? 'min-w-0 flex-1 text-xs' : 'text-sm',
      )}
    >
      {prompt}
    </p>
  );
}

function DropzoneButtonLabel({ label }: { label: ReactNode }) {
  return label ?? <T context="button to select a file for upload">Upload</T>;
}

function isMultiFile(props: Props): props is MultiFileProps {
  return props.multiple === true;
}

/**
 * Drag-and-drop (or click-to-select) area for one file, or several when
 * `multiple` is set.
 *
 * Owns the drag state, pulling files off the drop (including the Outlook-style
 * virtual-file fallback in `filesFromDrop`), gating drops against the same
 * `accept` list as the file picker, the "that wasn't a file" toast, and
 * turning a `validate` rejection into a toast.
 */
export function FileDropzone(props: Props) {
  const {
    accept,
    prompt,
    validate,
    disabled = false,
    inline = false,
    className,
    multiple = false,
    buttonLabel,
    children,
  } = props;
  const gt = useGT();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function toastRejections(rejections: UploadFileRejection[]): void {
    for (const rejection of rejections) {
      showToast({
        title: rejection.title,
        message: rejection.message,
        color: 'error',
      });
    }
  }

  async function acceptFiles(files: File[]): Promise<void> {
    const { accepted, rejections } = await acceptDroppedFiles(
      files,
      accept,
      validate,
      gt,
    );
    toastRejections(rejections);
    if (accepted.length === 0) return;
    if (isMultiFile(props)) {
      props.onFilesAccepted(accepted);
      return;
    }
    const [first] = accepted;
    if (first) props.onFileAccepted(first);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const dropped = filesFromDrop(e.dataTransfer);
    if (dropped.length === 0) {
      // No file in the drop — e.g. a webmail attachment chip or a link, which
      // carry only a URL/HTML string, not bytes. Tell the user how to proceed
      // rather than failing silently.
      showToast({
        title: gt('No file detected', {
          context: 'toast title when a drop contained no uploadable file',
        }),
        message: gt(
          'Drag a file from your file explorer, or save the email attachment first and drag that. Links and web content can’t be uploaded directly.',
          {
            context:
              'toast message when the user drops something that is not a file from their file system',
          },
        ),
        color: 'error',
      });
      return;
    }
    void acceptFiles(multiple ? dropped : dropped.slice(0, 1));
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(e.target.files ?? []);
    if (chosen.length > 0) void acceptFiles(chosen);
    // Reset so selecting the same file again re-triggers onChange.
    e.target.value = '';
  }

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragOver={(e) => {
        // Required every dragover for the element to be a valid drop target
        // (and so the browser doesn't open the file instead).
        e.preventDefault();
        if (disabled) return;
        e.dataTransfer.dropEffect = 'copy';
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'rounded-lg border-2 border-dashed transition-colors',
        inline
          ? 'flex items-center gap-2 px-2 py-1.5 text-left'
          : 'flex flex-col items-center justify-center gap-2 px-6 py-8 text-center',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/25',
        disabled && 'opacity-60',
        className,
      )}
    >
      <UploadSimpleIcon
        className={cn(
          'text-muted-foreground shrink-0',
          inline ? 'h-4 w-4' : 'h-8 w-8',
        )}
      />
      <DropzonePrompt prompt={prompt} inline={inline} />
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileInputChange}
      />
      <Button
        size={inline ? 'sm' : undefined}
        variant={inline ? 'outline' : undefined}
        className={inline ? 'shrink-0' : undefined}
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadSimpleIcon className={inline ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />
        <DropzoneButtonLabel label={buttonLabel} />
      </Button>
      {children}
    </div>
  );
}
